import React, { useState, useMemo, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { showSuccess } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { employeesService } from '@/services/employeesService'
import { accountManagementService } from '@/services/accountManagementService'
import { awardBonusService } from '@/services/awardBonusService'
import type { AwardBonusSavePayload } from '@/api/types/awardBonus.types'
import type { DepartmentLookup, Employee } from '@/api/types'
import type { Account } from '@/services/accountManagementService'

function getDefaultMonthYear(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export const AwardBonusDetail: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const isNew = id === 'new' || !id
  const editId = !isNew && id ? parseInt(id, 10) : 0

  const [departments, setDepartments] = useState<DepartmentLookup[]>([])
  const [employeesList, setEmployeesList] = useState<Employee[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [departmentId, setDepartmentId] = useState(0)
  const [employeeId, setEmployeeId] = useState(0)
  const [salaryMonthYear, setSalaryMonthYear] = useState(getDefaultMonthYear())
  const [bonusMonthYear, setBonusMonthYear] = useState(getDefaultMonthYear())
  const [debitAccountId, setDebitAccountId] = useState(0)
  const [amount, setAmount] = useState('')
  const [details, setDetails] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // Department: same as Incentive – employeesService.getDepartmentLookups (branch selected)
  useEffect(() => {
    if (!branchId) {
      setDepartments([])
      return
    }
    let cancelled = false
    employeesService.getDepartmentLookups().then((list) => {
      if (!cancelled && list) setDepartments(list)
    }).catch(() => { if (!cancelled) setDepartments([]) })
    return () => { cancelled = true }
  }, [branchId])

  // Employees: same as Incentive – branch-wise, filter by department
  useEffect(() => {
    if (!branchId) {
      setEmployeesList([])
      return
    }
    let cancelled = false
    employeesService.getAllEmployees(branchId, 1, 500).then((res) => {
      if (!cancelled && res?.data) setEmployeesList(res.data)
    }).catch(() => { if (!cancelled) setEmployeesList([]) })
    return () => { cancelled = true }
  }, [branchId])

  // Debit accounts: same as Incentive – accountManagementService.getAllAccounts
  useEffect(() => {
    if (!branchId) {
      setAccounts([])
      return
    }
    let cancelled = false
    accountManagementService.getAllAccounts(branchId).then((list) => {
      if (!cancelled) setAccounts(list)
    }).catch(() => { if (!cancelled) setAccounts([]) })
    return () => { cancelled = true }
  }, [branchId])

  // Load record in edit mode
  useEffect(() => {
    if (isNew || !branchId || !editId) return
    let cancelled = false
    setLoading(true)
    awardBonusService.getById(editId, branchId).then((data) => {
      if (!cancelled) {
        setEmployeeId(data.employeeId ?? 0)
        setDepartmentId(data.departmentId ?? 0)
        setSalaryMonthYear(data.salaryMonthYear ?? getDefaultMonthYear())
        setBonusMonthYear(data.bonusMonthYear ?? getDefaultMonthYear())
        setDebitAccountId(data.debitAccount ?? 0)
        setAmount(String(data.amount ?? ''))
        setDetails(data.details ?? '')
      }
    }).catch((err) => {
      if (!cancelled) {
        toast.error(err.response?.data?.message || 'Failed to load award bonus.')
        navigate('/payroll/award-bonus')
      }
    }).finally(() => {
      if (!cancelled) setLoading(false)
    })
    return () => { cancelled = true }
  }, [isNew, branchId, editId, navigate])

  const departmentOptions = useMemo(
    () => [
      { value: '0', label: 'All' },
      ...departments.filter((d) => d.id).map((d) => ({
        value: String(d.id),
        label: d.lookupNameEn || d.lookupNameAr || String(d.id),
      })),
    ],
    [departments]
  )

  const employeeOptions = useMemo(() => {
    const nameFor = (emp: Employee) =>
      [emp.empFirstNameEn, emp.empFamilyNameEn].filter(Boolean).join(' ').trim() || emp.empCode || '—'
    const list = departmentId
      ? employeesList.filter((e) => e.empDepartmetId === departmentId && e.id && e.empCode)
      : employeesList.filter((e) => e.id && e.empCode)
    return list.map((e) => ({ value: String(e.id), label: nameFor(e) }))
  }, [employeesList, departmentId])

  const debitAccountOptions = useMemo(
    () =>
      accounts.map((acc) => {
        const code = acc.accountCode || ''
        const name = acc.accountNameEn || acc.accountName || acc.accountNameAr || `Account ${acc.id}`
        return { value: String(acc.id || 0), label: code ? `${code} - ${name}` : name }
      }),
    [accounts]
  )

  const handleSave = async () => {
    if (!branchId) {
      toast.error('Please select a branch.')
      return
    }
    if (!employeeId) {
      toast.error('Please select an employee.')
      return
    }
    if (!salaryMonthYear || salaryMonthYear.length < 7) {
      toast.error('Please select Salary Month/Year.')
      return
    }
    if (!bonusMonthYear || bonusMonthYear.length < 7) {
      toast.error('Please select Bonus Month/Year.')
      return
    }
    if (!debitAccountId) {
      toast.error('Please select Debit Account.')
      return
    }
    const amt = parseFloat(amount)
    if (Number.isNaN(amt) || amt < 0) {
      toast.error('Please enter a valid amount.')
      return
    }
    if (!details.trim()) {
      toast.error('Details is required.')
      return
    }

    setSaving(true)
    try {
      const payload: AwardBonusSavePayload = {
        id: isNew ? 0 : editId,
        empId: employeeId,
        bonusAmount: amt,
        monthYear: bonusMonthYear,
        detail: details.trim(),
        departmentId: departmentId || 0,
        debitAccount: debitAccountId,
        salaryMonthYear,
        branchId,
      }
      console.log('=== Award Bonus API Payload (Save/Update) ===')
      console.log(JSON.stringify(payload, null, 2))
      console.log('============================================')
      if (isNew) {
        await awardBonusService.save(payload)
        await showSuccess('Saved', 'Award bonus saved successfully.')
      } else {
        await awardBonusService.update(payload)
        await showSuccess('Updated', 'Award bonus updated successfully.')
      }
      navigate('/payroll/award-bonus')
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Save failed.')
    } finally {
      setSaving(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader title="Award Bonus" description="Award Bonus Detail" />
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Please select a branch to manage award bonus.
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader title="Award Bonus" description="Award Bonus Detail" />

      <Card>
        <CardHeader>
          <CardTitle>Award Bonus Detail</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="py-8 text-center text-muted-foreground">Loading...</div>
          ) : (
            <>
              {/* Row 1: Department, Employee, Salary Month/Year */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Department *</Label>
                  <SearchableSelect
                    options={departmentOptions}
                    value={departmentId ? String(departmentId) : '0'}
                    onValueChange={(v) => {
                      setDepartmentId(v ? Number(v) : 0)
                      setEmployeeId(0)
                    }}
                    placeholder="All"
                    searchPlaceholder="Search..."
                    emptyMessage="No department"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Employee *</Label>
                  <SearchableSelect
                    options={employeeOptions}
                    value={employeeId ? String(employeeId) : ''}
                    onValueChange={(v) => setEmployeeId(v ? Number(v) : 0)}
                    placeholder="Select employee"
                    searchPlaceholder="Search..."
                    emptyMessage="No employee"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Salary Month/Year *</Label>
                  <Input
                    type="month"
                    value={salaryMonthYear}
                    onChange={(e) => setSalaryMonthYear(e.target.value)}
                  />
                </div>
              </div>

              {/* Row 2: Bonus Month/Year, Debit Account */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Bonus Month/Year *</Label>
                  <Input
                    type="month"
                    value={bonusMonthYear}
                    onChange={(e) => setBonusMonthYear(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Debit Account *</Label>
                  <SearchableSelect
                    options={debitAccountOptions}
                    value={debitAccountId ? String(debitAccountId) : ''}
                    onValueChange={(v) => setDebitAccountId(v ? Number(v) : 0)}
                    placeholder="Select account"
                    searchPlaceholder="Search accounts..."
                    emptyMessage="No accounts"
                  />
                </div>
              </div>

              {/* Row 3: Amount */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Amount *</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="Amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>
              </div>

              {/* Row 4: Details */}
              <div className="space-y-2">
                <Label>Details *</Label>
                <Textarea
                  placeholder="Details"
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  rows={4}
                />
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => navigate('/payroll/award-bonus')}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
